import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BehaviorSubject, switchMap, filter, take } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { MatDialog } from '@angular/material/dialog';

import { User } from 'src/app/models/user';
import { AclService } from 'src/app/services/acl.service';
import { UserService } from 'src/app/services/user.service';
import { EmailSearchComponent } from 'src/app/ui/email-search/email-search.component';
import { isEventCancelled, isEventCompleted, isEventEnded } from 'src/app/utilities/events.utils';
import { ReassignUserDialogComponent } from 'src/app/dialogs/reassign-user-dialog/reassign-user-dialog.component';
import { EventService } from 'src/app/services/event.service';
import { NotificationToasterService } from 'src/app/services/notification-toaster.service';
import { UserSearchSelectionService } from '../../../services/user-search-selection.service';
import { Event, RSVPStatusEnum } from '../../../models/event';
import { FeatureManagementService } from '../../../services/feature-management.service';
import { AttendanceUser } from '../../../sessions/session/attendance/attendance-ui-models';
import { AttendanceStatusMode } from '../../../common/attendance-status-indicator/attendance-status-indicator.component';

export enum InvitedSectionType {
  ORGANIZER = 'organizer',
  ATTENDEE = 'attendee',
  AUTO_ADDED = 'auto_added',
}

interface InvitedSectionTypeConfig {
  addButtonDataName?: string;
  addButtonLabel?: string;
  showFindButton?: boolean;
  findButtonDataName?: string;
  findButtonLabel?: string;
}

@UntilDestroy()
@Component({
  selector: 'app-event-attendees',
  templateUrl: './event-attendees.component.html',
  styleUrls: ['./event-attendees.component.scss'],
})
export class EventAttendeesComponent implements OnInit, OnChanges {
  @Input() set attendees(value: User[]) {
    this.attendees$.next(value ?? []);
  }
  @Input() eventAttendanceUsersMap: Record<string, AttendanceUser> = {};
  @Input() canEditAttendees = false;
  @Input() showAttendance = false;
  @Input() canEditAttendance = false;
  @Input() userType: InvitedSectionType = InvitedSectionType.ATTENDEE;
  @Input() currentEvent!: Event;
  @Input() allowDeclinedStatusView = false;
  @Input() isEditOrCreate = false;
  @Output() showSelectAttendeeDialog = new EventEmitter();

  @ViewChild('emailSearchComp') emailSearchComp?: EmailSearchComponent;

  readonly INVITED_SECTION_TYPE_CONFIG: Record<InvitedSectionType, InvitedSectionTypeConfig>;

  currentUser?: User;
  isAddingUser = false;
  isInvitingUser = false;
  invitedSectionType = InvitedSectionType;
  shouldShowSubtitle = false;
  shouldShowEmail = false;
  invitedSectionTypeConfig: InvitedSectionTypeConfig = {};
  userDeclinedData: Record<string, { reason?: string }> = {};
  cohortUsersData = new Set<string>();
  isReassignAllowed = false;
  attendees$ = new BehaviorSubject<User[]>([]);

  readonly AttendanceStatusMode = AttendanceStatusMode;

  constructor(
    private userService: UserService,
    public aclService: AclService,
    private featureManagementService: FeatureManagementService,
    private translate: TranslateService,
    private notificationToasterService: NotificationToasterService,
    private dialog: MatDialog,
    private eventService: EventService,
    private translateService: TranslateService,
    private userSearchSelectionService: UserSearchSelectionService,
  ) {
    this.INVITED_SECTION_TYPE_CONFIG = this.initInvitedSectionTypeConfig();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isEditOrCreate']) {
      this.checkIsReassignAllowed();
    }
  }

  ngOnInit(): void {
    this.userService.user.pipe(untilDestroyed(this)).subscribe((userData) => {
      this.currentUser = userData?.user;
      if (!this.currentUser) {
        return;
      }
      // Show if user is creator of event OR if (user is not a student in institution AND event is associated with same institution)
      this.shouldShowSubtitle =
        (!this.aclService.isStudent(this.currentUser) &&
          this.currentEvent.institution_id === this.currentUser.institution?._id) ||
        this.currentUser._id === this.currentEvent.creator;

      // Show if user is creator of event OR if (user is an admin in institution AND event is associated with same institution)
      this.shouldShowEmail =
        (this.aclService.isAdmin(this.currentUser) &&
          this.currentEvent.institution_id === this.currentUser.institution?._id) ||
        this.currentUser._id === this.currentEvent.creator;

      // Update declined status after currentUser is available
      this.updateDeclinedUsers();

      // Update cohort users data
      this.updateCohortUsersData();

      // Check if reassign is allowed
      this.checkIsReassignAllowed();
    });

    this.attendees$.pipe(untilDestroyed(this)).subscribe(() => this.checkIsReassignAllowed());

    this.invitedSectionTypeConfig = this.INVITED_SECTION_TYPE_CONFIG[this.userType];
  }

  /**
   * Determines whether user reassignment is allowed for this event.
   * Sets the isReassignAllowed flag based on user permissions and event state.
   *
   * Reassignment is allowed when:
   * 1. For organizer sections: There are multiple organizers (>1)
   * 2. For admin/site admin users: All of the following conditions must be met:
   *    - User has admin or site admin privileges
   *    - Component is not in edit mode
   *    - Event has not ended yet
   *    - Event has not been cancelled
   *    - Event has not been completed
   *
   * @returns void - Updates the isReassignAllowed property
   */
  private checkIsReassignAllowed(): void {
    if (!this.currentUser) {
      return;
    }

    this.isReassignAllowed =
      this.aclService.isAdminOrSiteAdmin(this.currentUser) &&
      !this.isEditOrCreate &&
      !isEventEnded(this.currentEvent) &&
      !isEventCancelled(this.currentEvent) &&
      !isEventCompleted(this.currentEvent) &&
      this.userType !== InvitedSectionType.AUTO_ADDED &&
      (this.userType !== InvitedSectionType.ORGANIZER || this.attendees$.value.length > 1);
  }

  removeUser(user: User) {
    this.attendees$.next(this.attendees$.value.filter((u) => u._id !== user._id));
    // Remove user from excluded list so they can appear in search results again
    this.userSearchSelectionService.removeSelectedUsers([user]);
  }

  handleEmailValues({ emails }: { emails: string[] }): void {
    if (emails.length) {
      this.isInvitingUser = true;
      this.userService
        .inviteUsers(emails)
        .pipe(take(1))
        .subscribe((users: User[]) => {
          users.forEach((user) => {
            this.selectUser(user);
          });
          this.isInvitingUser = false;
          this.isAddingUser = false;
        });
    }
  }

  selectUser(user: User): void {
    // checking if user is already selected
    if (!this.userSearchSelectionService.isSelected(user)) {
      this.attendees$.next([...this.attendees$.value, user]);
      // Add user to selected list so they don't appear in search results
      this.userSearchSelectionService.addSelectedUsers([user]);
    }
  }

  getAttendees(): User[] {
    return this.attendees$.value;
  }

  handleInputBlur(): void {
    this.isAddingUser = this.unsavedAttendeesExist();
  }

  unsavedAttendeesExist(): boolean {
    return !!this.emailSearchComp?.searchQuery;
  }

  private initInvitedSectionTypeConfig(): Record<InvitedSectionType, InvitedSectionTypeConfig> {
    const user = this.userService.user.value?.user;
    const isAdminOrSiteAdmin = !!user && this.aclService.isAdminOrSiteAdmin(user);
    const isAvailabilityEnabled = this.featureManagementService.isAvailabilityEnabled();
    const isFindButtonEnabled = isAdminOrSiteAdmin && isAvailabilityEnabled;

    return {
      [InvitedSectionType.ORGANIZER]: {
        addButtonDataName: 'events-add-organizer',
        addButtonLabel: this.translate.instant('Add organizer'),
        showFindButton: isFindButtonEnabled,
        findButtonDataName: 'events-availability-search-hosts',
        findButtonLabel: this.translate.instant('Find available organizers'),
      },
      [InvitedSectionType.ATTENDEE]: {
        addButtonDataName: 'events-add-attendee',
        addButtonLabel: this.translate.instant('Add attendee'),
        showFindButton: isFindButtonEnabled,
        findButtonDataName: 'events-availability-search-participants',
        findButtonLabel: this.translate.instant('Find attendees'),
      },
      [InvitedSectionType.AUTO_ADDED]: {},
    };
  }

  /**
   * Computes the declined user status map for the event.
   * Creates userDeclinedStatusMap for template use.
   * Should be called when currentUser or event data changes.
   */
  private updateDeclinedUsers(): void {
    this.userDeclinedData = {};

    this.currentEvent?.users_rsvp_status?.forEach((rsvp) => {
      if (rsvp.status === RSVPStatusEnum.NO) {
        // Show decline status if:
        // 1. User can view all decline statuses (organizer/admin) OR
        // 2. It's the current user's own decline status
        const canViewThis =
          this.allowDeclinedStatusView ||
          rsvp.user_id?.toString() === this.currentUser?._id?.toString();

        if (canViewThis) {
          this.userDeclinedData[rsvp.user_id] = { reason: rsvp.note };
        }
      }
    });
  }

  private updateCohortUsersData(): void {
    const cohort = this.currentEvent?.program_data?.cohort;
    const userIds = [...(cohort?.organizers || []), ...(cohort?.attendees || [])];
    this.cohortUsersData = new Set(userIds);
  }

  showReassignUserDialog(userId: string): void {
    const user = this.attendees$.value.find((u) => u._id === userId)!;

    this.dialog
      .open(ReassignUserDialogComponent, {
        panelClass: 'select-entity-dialog',
        autoFocus: false,
        restoreFocus: false,
        disableClose: true,
        data: { event: this.currentEvent },
      })
      .afterClosed()
      .pipe(
        take(1),
        filter((targetEvent) => !!targetEvent),
        switchMap((targetEvent: Event) =>
          this.eventService
            .reassignUser(this.currentEvent._id, targetEvent._id, user._id)
            .pipe(take(1)),
        ),
      )
      .subscribe({
        next: () => {
          const message =
            this.userType === InvitedSectionType.ORGANIZER
              ? this.translateService.instant('Organizer successfully reassigned')
              : this.translateService.instant('Attendee successfully reassigned');

          this.notificationToasterService.showDefaultSuccessNotification(
            message,
            this.notificationToasterService.generateNotificationCode(),
            true,
          );

          this.removeUser(user);
          this.eventService.triggerEventsRefresh();
        },
        error: () => {
          const message =
            this.userType === InvitedSectionType.ORGANIZER
              ? this.translateService.instant('Organizer reassignment failed')
              : this.translateService.instant('Attendee reassignment failed');

          this.notificationToasterService.showDefaultErrorNotificationV2(
            message,
            '',
            this.notificationToasterService.generateNotificationCode(),
            undefined,
            undefined,
            undefined,
            true,
          );
        },
      });
  }
}
